// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 39-gcp-installation.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md

import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StepHeader } from '@/components/install/StepHeader';
import { DraftNote, useInstallDraft } from '@/components/install/installDraft';
import {
  DisplayNameEditor,
  useDisplayNameEditor,
} from '@/components/greenlight/org/DisplayNameEditor';
import {
  SupportedAgentsEditor,
  useSupportedAgentsEditor,
} from '@/components/greenlight/org/SupportedAgentsEditor';
import {
  ALL_AGENT_KINDS,
  ORG_SLUG_REGEX,
  orgSlugFromName,
  type AgentKind,
  type InstallationState,
} from '@greenlight/shared';
import {
  InstallerEmailSchema,
  type InstallCloud,
  type InstallContextSchema,
  type InstallDraft,
} from '@greenlight/contract';
import type { z } from 'zod';

type InstallContext = z.infer<typeof InstallContextSchema>;

// Provisioning-tool name per provider, keyed by the contract's cloud
// discriminator (docs/39) so a new cloud only needs an entry here.
const PROVISIONER_NAME: Record<InstallCloud['provider'], string> = {
  azure: 'Bicep',
  gcp: 'Terraform',
  aws: 'Terraform',
  localfake: 'the local dev bootstrap',
};

interface OrgSetupStepProps {
  mode: 'create' | 'review';
  context: InstallContext;
  org?: {
    name: string;
    slug: string;
    appsSubdomain: string | null;
    cloud: InstallCloud;
    supportedAgents: AgentKind[];
  };
  installationState?: InstallationState;
  onCreate: (input: {
    name: string;
    slug: string;
    supportedAgents: AgentKind[];
    installerEmail: string;
  }) => Promise<boolean>;
  /** Resolves false when the server refused, so the failure surfaces here. */
  onNext: () => Promise<boolean>;
  isSubmitting: boolean;
  /** A prefill draft to show for confirmation in create mode. */
  draft?: InstallDraft['organization'];
  onDraftTouched?: () => void;
}

const AGENT_LABELS: Record<AgentKind, string> = {
  claude: 'Claude',
  codex: 'Codex',
  cursor: 'Cursor',
  copilot: 'Copilot',
  other: 'Other',
};

// Stable reference: a fresh literal recreated per render would re-trigger the
// editor's reset effect on every render in create mode (no `org` yet).
const ALL_AGENTS_DEFAULT: AgentKind[] = [...ALL_AGENT_KINDS];

export function OrgSetupStep({
  mode,
  context,
  org,
  installationState,
  onCreate,
  onNext,
  isSubmitting,
  draft: incomingDraft,
  onDraftTouched,
}: OrgSetupStepProps) {
  const { draft, markTouched } = useInstallDraft(
    mode === 'create' ? incomingDraft : undefined,
    onDraftTouched,
  );
  const nameEditor = useDisplayNameEditor(org?.name ?? draft?.displayName ?? '');
  const agentsEditor = useSupportedAgentsEditor(
    org?.supportedAgents ?? draft?.supportedAgents ?? ALL_AGENTS_DEFAULT,
  );
  const displayNameEditor = {
    ...nameEditor,
    setDisplayName: (value: string) => {
      markTouched();
      nameEditor.setDisplayName(value);
    },
  };
  const supportedAgentsEditor = {
    ...agentsEditor,
    toggle: (agent: AgentKind) => {
      markTouched();
      agentsEditor.toggle(agent);
    },
  };
  const [slug, setSlug] = React.useState(org?.slug ?? '');
  // Once IT types a slug of their own, the display name stops driving it.
  const [slugEdited, setSlugEdited] = React.useState(false);
  const [installerEmail, setInstallerEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const derivedSlug = orgSlugFromName(displayNameEditor.displayName);
  const effectiveSlug = slugEdited ? slug.trim() : derivedSlug;

  const handleNext = async (): Promise<void> => {
    setError(null);
    if (!(await onNext())) {
      setError('Could not save this step. Check the control plane is running and try again.');
    }
  };

  if (mode === 'review' && org) {
    const details = installationState?.steps?.infrastructure?.details as
      | Record<string, string>
      | undefined;
    const savedInstallerEmail = installationState?.steps?.org_setup?.details?.['installer_email'];

    return (
      <div className="space-y-6" data-testid="step-org_setup">
        <StepHeader step="org_setup" title="Organization">
          This install is bound to a single organization. {PROVISIONER_NAME[org.cloud.provider]}{' '}
          provisioned the resources below. Review them or continue to the next step.
        </StepHeader>

        <dl className="divide-y divide-border rounded-lg border text-sm">
          <Row label="Display name" value={org.name} />
          {typeof savedInstallerEmail === 'string' && (
            <Row label="Your work email" value={savedInstallerEmail} />
          )}
          <Row label="Org slug" value={org.slug} />
          <Row label="Greenlight host" value={org.appsSubdomain ?? context.appsDomain} />
          <Row label="Greenlight URL" value={context.greenlightBaseUrl} />
          <CloudRows cloud={org.cloud} />
          <Row
            label="Supported agents"
            value={org.supportedAgents.map((agent) => AGENT_LABELS[agent]).join(', ')}
          />
          {details?.ingressTarget && <Row label="Ingress target" value={details.ingressTarget} />}
          {details?.dnsZoneNameservers && (
            <Row label="DNS zone nameservers" value={details.dnsZoneNameservers} />
          )}
        </dl>

        {error && (
          <Alert variant="destructive" data-testid="org-setup-next-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="button"
          className="w-full md:w-auto h-11 md:h-9"
          onClick={() => void handleNext()}
          disabled={isSubmitting}
          data-testid="step-next"
        >
          {isSubmitting ? 'Saving…' : 'Next'}
        </Button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = displayNameEditor.validate();
    if (trimmedName === null) return;
    if (!effectiveSlug) {
      setError(
        slugEdited
          ? 'Org slug is required.'
          : 'This name has no valid slug. Edit the slug below: lowercase letters, digits, and hyphens, starting with a letter (3–32 characters).',
      );
      return;
    }
    if (!ORG_SLUG_REGEX.test(effectiveSlug)) {
      setError(
        'Slug must be lowercase letters, digits, and hyphens, starting with a letter (3–32 characters).',
      );
      return;
    }
    const supportedAgents = supportedAgentsEditor.validate();
    if (supportedAgents === null) return;
    const email = InstallerEmailSchema.safeParse(installerEmail);
    if (!email.success) {
      setError('Enter your work email address.');
      return;
    }
    const ok = await onCreate({
      name: trimmedName,
      slug: effectiveSlug,
      supportedAgents,
      installerEmail: email.data,
    });
    if (!ok)
      setError('Could not create the organization. It may already exist — refresh and continue.');
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit} data-testid="step-org_setup">
      <StepHeader step="org_setup" title="Set up your organization">
        Create the org record for this Greenlight install. Cloud resources are already provisioned;
        this step registers the organization in the control plane.
      </StepHeader>

      {draft && <DraftNote />}

      <div className="space-y-4">
        <DisplayNameEditor
          editor={displayNameEditor}
          id="org-name"
          inputTestId="org-setup-name"
          disabled={isSubmitting}
          description="Shown across this Greenlight installation."
        />

        <div className="space-y-2">
          <Label htmlFor="org-installer-email">Your work email</Label>
          <Input
            id="org-installer-email"
            type="email"
            autoComplete="email"
            value={installerEmail}
            placeholder="it-admin@acme.com"
            onChange={(e) => {
              markTouched();
              setInstallerEmail(e.target.value);
            }}
            disabled={isSubmitting}
            data-testid="org-setup-installer-email"
          />
          <p className="text-sm text-muted-foreground">
            Fills in the initial org admin and the Shift contact in later steps. You can change
            either one there.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-slug">Org slug</Label>
          <Input
            id="org-slug"
            value={effectiveSlug}
            onChange={(e) => {
              markTouched();
              setSlugEdited(true);
              setSlug(e.target.value);
            }}
            pattern="[a-z][a-z0-9-]{1,30}[a-z0-9]"
            data-testid="org-setup-slug"
          />
          <p className="text-sm text-muted-foreground">
            Derived from the display name and used in URLs and identifiers. Edit it if you need a
            specific one.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Supported agents</Label>
          <SupportedAgentsEditor editor={supportedAgentsEditor} disabled={isSubmitting} />
        </div>

        <div className="space-y-2">
          <Label>Greenlight host</Label>
          <p className="text-sm font-medium" data-testid="org-setup-apps-domain">
            <MonoValue copyable wrap>
              {context.appsDomain}
            </MonoValue>
          </p>
        </div>

        {hasCloudRows(context.cloud) && (
          <dl className="divide-y divide-border rounded-lg border text-sm">
            <CloudRows cloud={context.cloud} />
          </dl>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full md:w-auto h-11 md:h-9"
        disabled={isSubmitting}
        data-testid="org-setup-submit"
      >
        {isSubmitting ? 'Creating…' : 'Create organization'}
      </Button>
    </form>
  );
}

/** The provisioned resources this cloud names. `Row` drops the empty ones. */
function CloudRows({ cloud }: { cloud: InstallCloud }): React.JSX.Element {
  switch (cloud.provider) {
    case 'azure':
      return (
        <>
          <Row label="Resource group" value={cloud.resourceGroup} />
          <Row label="Subscription ID" value={cloud.subscriptionId} />
        </>
      );
    case 'gcp':
      return <Row label="Project" value={cloud.projectId} />;
    case 'aws':
      return (
        <>
          <Row label="Account ID" value={cloud.accountId} />
          <Row label="Region" value={cloud.region} />
          <Row label="EKS cluster" value={cloud.eksClusterName} />
          <Row label="ECR registry" value={cloud.ecrRegistryHost} />
          <Row label="Route 53 zone" value={cloud.route53ZoneId} />
        </>
      );
    default:
      return <></>;
  }
}

function hasCloudRows(cloud: InstallCloud): boolean {
  switch (cloud.provider) {
    case 'azure':
      return Boolean(cloud.resourceGroup || cloud.subscriptionId);
    case 'gcp':
      return Boolean(cloud.projectId);
    case 'aws':
      return Boolean(cloud.accountId || cloud.region || cloud.eksClusterName);
    default:
      return false;
  }
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
      <dt className="text-muted-foreground w-full sm:w-40 shrink-0">{label}</dt>
      <dd className="font-medium min-w-0">
        <MonoValue copyable wrap>
          {value}
        </MonoValue>
      </dd>
    </div>
  );
}
